export import React = require('react');
export import models = require("app/models/requireme");
export import kea = require('node_modules/kea-react/dist/index');
import { omit, rxFlatten, toObservable, shallowEquals, deepEquals } from "keautils";
import * as rx from "rxjs";

import { createSelectorRx, createDeepSelectorRx } from 'keautils';
import { componentToRx, RxfyScalar } from 'react-rxk';
import { SelectorValue, SelectorValueNotNull } from 'kea-react/dist/selector/types';

export interface SelectorSimpleProps<T> {
    items?: PromiseLike<T[] | undefined> | rx.Observable<T[] | undefined> | (T[] | undefined);
    value?: RxfyScalar<number | null | undefined>;
    onChange?: (x: number | null) => void;
    label?: string;
    readonly?: boolean;
    error?: kea.FieldError;
    loading?: boolean;
    onAgregar?: () => void | Promise<void>;
    onEditar?: (value: number) => void | Promise<void>;
    onBorrar?: (value: number) => void | Promise<void>;
}

/**Propiedades de un selector filtrado en el servidor */
export interface SelectorFactoryProps<T, TFiltro> extends SelectorSimpleProps<T> {
    filtro?: TFiltro;
}


interface SimpleSelectorViewProps<T> extends SelectorSimpleProps<T> {
    getValue: (item: T) => SelectorValueNotNull;
    getLabel: (item: T) => string | { label: string, view: JSX.Element };
    defaultItems: PromiseLike<T[]> | T[] | rx.Observable<T[]>;

}
class SimpleSelectorView extends React.PureComponent<SimpleSelectorViewProps<any>> {
    //Es importante utilizar estos selectores para que no se este recalculando el observable con cada render:
    items = (props: SimpleSelectorViewProps<any>) => props.items;
    defaultItems = (props: SimpleSelectorViewProps<any>) => props.defaultItems;
    getValue = (props: SimpleSelectorViewProps<any>) => props.getValue;
    getLabel = (props: SimpleSelectorViewProps<any>) => props.getLabel;
    mapFunc = createSelectorRx(this.getValue, this.getLabel, (getValue, getLabel) => (x: any) => {
        const view = getLabel(x);
        const value = getValue(x);
        if (typeof view == "string") {
            return {
                value,
                label: view
            };
        } else {
            return {
                value,
                label: view.label,
                view: view.view
            };
        }
    });
    itemsMapped = createSelectorRx(this.items, this.mapFunc, this.defaultItems, (items, mapFunc, defaultItems) =>
        toObservable(this.props.items || this.props.defaultItems).map(x => (x || []).map(mapFunc)));

    render() {
        const props = this.props;
        const { getLabel } = this.props;

        const items = this.itemsMapped(this.props);
        const label = this.props.label;

        return (
            <kea.Select
                value={props.value}
                items={items}
                readonly={props.readonly}
                onChange={props.onChange}
                label={label}
                error={props.error}
                loading={props.loading}
                onAgregar={props.onAgregar}
                onEditar={props.onEditar}
                onBorrar={props.onBorrar}
            />
        );
    }
}

/**Crea un componente de selector */
export function SelectorSimpleFactory<T, TValue extends SelectorValueNotNull>(defaultItems: PromiseLike<T[]> | T[] | rx.Observable<T[]>, 
    getValue: (item: T) => TValue, getLabel: (item: T) => string | ({ label: string, view: JSX.Element })) {
    return (props: SelectorSimpleProps<T>) =>
        <SimpleSelectorView {...props} defaultItems={defaultItems} getLabel={getLabel} getValue={getValue} />;
}

export interface SelectorFiltroProps<T, TFiltro> extends SelectorSimpleProps<T> {
    filtro?: RxfyScalar<TFiltro | undefined>
}
/**Crea un componente de selector que sus items son obtenidos dependiendo del valor los parametros de filtro */
export function SelectorFiltradoFactory<T, TFiltro>
    (defaultItemsFunc: (filtro: TFiltro | undefined) => (Promise<T[]> | rx.Observable<T[]> | T[]), getValue: (item: T) => number, getLabel: (item: T) => string | ({ label: string, view: JSX.Element }))
    : React.ComponentClass<SelectorFiltroProps<T, TFiltro>> {
    type TProps = SelectorSimpleProps<T> & { filtro?: TFiltro };

    const ret = class SelectorFiltradoComponent extends React.PureComponent<TProps> {
        private propsSelector = (x: TProps) => x;
        private filtro = createSelectorRx(this.propsSelector, x => x.filtro)
        private items = createDeepSelectorRx(this.filtro, x => defaultItemsFunc(x));

        render() {
            const items = this.items(this.props);
            return <SimpleSelectorView {... this.props} defaultItems={items} getLabel={getLabel} getValue={getValue} />
        }
    }
    const retRx = componentToRx(ret, undefined, undefined, {
        items: { ignore: { observable: true, promise: true } },
        loading: { loading: true }
    }) as any;

    return retRx;
}

/**Crea un componente selector a partir de un enum */
export function SelectorEnumFactory<T>(enumType: T) {
    const items = Object.keys(enumType).filter(x => +x === +x).map(x => ({ value: +x, label: enumType[x] }));
    return SelectorSimpleFactory(items, x => x.value, x => x.label);
}


// WEBPACK FOOTER //
// ./app/features/selectores/logic.tsx